/**
 * client/roulette.ts
 * Responsabilidad: Motor Canvas 2D autoritativo para la Ruleta Alquímica Diaria y Célula Reactiva Central.
 * Dibuja 12 segmentos mágicos con bordes dorados, aguja top-center, desaceleración ease-out-cubic de 5.5s,
 * ticks de audio por rebanada y expresiones faciales reactivas de la Célula de Fuego 🔥.
 */

import { ROULETTE_SEGMENTS, type RouletteSegment } from "../src/shared/constants";
import { AudioManager } from "./audio";

export type CellMood = "idle" | "spinning" | "won_crystals" | "won_orbs" | "won_skin" | "empty";

export class RouletteWheel {
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;
    private audio: AudioManager;

    private currentAngle: number = 0; // en grados
    private isSpinning: boolean = false;
    private cellMood: CellMood = "idle";
    private rafId: number | null = null;
    private lastTickIndex: number = -1;
    private segments: RouletteSegment[] = [...ROULETTE_SEGMENTS];

    // Partículas y destellos
    private sparkles: Array<{
        x: number;
        y: number;
        vx: number;
        vy: number;
        color: string;
        size: number;
        alpha: number;
        life: number;
    }> = [];

    constructor(canvas: HTMLCanvasElement, audio?: AudioManager) {
        this.canvas = canvas;
        const context = canvas.getContext("2d");
        if (!context) {
            throw new Error("[RouletteWheel] No se pudo obtener el contexto 2D del Canvas");
        }
        this.ctx = context;
        this.audio = audio || new AudioManager();

        this.startRenderLoop();
    }

    public setMood(mood: CellMood): void {
        this.cellMood = mood;
        if (mood === "won_crystals" || mood === "won_orbs" || mood === "won_skin") {
            this.spawnCelebrationSparkles(mood);
        }
    }

    public getMood(): CellMood {
        return this.cellMood;
    }

    public isWheelSpinning(): boolean {
        return this.isSpinning;
    }

    public setSegments(newSegments: RouletteSegment[]): void {
        if (Array.isArray(newSegments) && newSegments.length > 0) {
            this.segments = newSegments;
        }
    }

    /**
     * Hace girar la ruleta hasta detenerse exactamente en targetIndex (0-11).
     * Duración: 5.5s con curva de desaceleración cúbica elástica.
     */
    public spinTo(targetIndex: number): Promise<void> {
        if (this.isSpinning) {
            return Promise.resolve();
        }

        this.isSpinning = true;
        this.setMood("spinning");

        return new Promise<void>((resolve) => {
            const numSlices = this.segments.length;
            const sliceDegrees = 360 / numSlices;
            const startAngle = this.currentAngle % 360;
            const fullSpins = 6 + Math.floor(Math.random() * 2); // 6 o 7 giros completos
            // targetAngle exacto para que el centro del segmento targetIndex descanse bajo la aguja superior (270° / top)
            const stopAngleInCircle = (360 - (targetIndex * sliceDegrees + sliceDegrees / 2)) % 360;
            const totalDistance = fullSpins * 360 + ((stopAngleInCircle - startAngle + 360) % 360);
            const targetFinalAngle = startAngle + totalDistance;

            const durationMs = 5500;
            const startTime = performance.now();

            const step = (now: number) => {
                const elapsed = now - startTime;
                const progress = Math.min(1, elapsed / durationMs);

                // Desaceleración cúbica ultra suave: 1 - (1 - t)^3
                const ease = 1 - Math.pow(1 - progress, 3);
                this.currentAngle = startAngle + totalDistance * ease;

                // Detección de paso de gajos para ticks sonoros
                // La aguja superior apunta a 270° relativo a 0°
                const needleNormalizedAngle = (360 - (this.currentAngle % 360) + 360) % 360;
                const currentSegment = Math.floor(needleNormalizedAngle / sliceDegrees);
                if (currentSegment !== this.lastTickIndex) {
                    this.lastTickIndex = currentSegment;
                    try {
                        this.audio.playEatSfx();
                    } catch {
                        // ignore audio context restrictions
                    }
                }

                if (progress < 1) {
                    requestAnimationFrame(step);
                } else {
                    this.currentAngle = targetFinalAngle % 360;
                    this.isSpinning = false;

                    // Expresión ganadora según el segmento
                    const seg = this.segments[targetIndex] || this.segments[0];
                    if (seg.type === "crystals") {
                        this.setMood("won_crystals");
                    } else if (seg.type === "orbs") {
                        this.setMood("won_orbs");
                    } else if (seg.type === "skin") {
                        this.setMood("won_skin");
                    } else {
                        this.setMood("empty");
                    }

                    resolve();
                }
            };

            requestAnimationFrame(step);
        });
    }

    private startRenderLoop(): void {
        const render = () => {
            this.draw();
            this.rafId = requestAnimationFrame(render);
        };
        this.rafId = requestAnimationFrame(render);
    }

    public destroy(): void {
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
    }

    /**
     * Dibuja el marco completo de la ruleta, gajos, aguja y la Célula de Fuego central.
     */
    public draw(): void {
        const ctx = this.ctx;
        const w = this.canvas.width;
        const h = this.canvas.height;
        const cx = w / 2;
        const cy = h / 2;
        const radius = Math.min(cx, cy) - 16;
        const now = performance.now();

        ctx.clearRect(0, 0, w, h);

        // 1. Resplandor dorado místico exterior
        const outerGlow = ctx.createRadialGradient(cx, cy, radius * 0.85, cx, cy, radius * 1.08);
        outerGlow.addColorStop(0, "rgba(245, 158, 11, 0.28)");
        outerGlow.addColorStop(0.5, "rgba(217, 119, 6, 0.12)");
        outerGlow.addColorStop(1, "rgba(0, 0, 0, 0)");
        ctx.fillStyle = outerGlow;
        ctx.beginPath();
        ctx.arc(cx, cy, radius * 1.08, 0, Math.PI * 2);
        ctx.fill();

        // 2. Dibujar los gajos giratorios
        ctx.save();
        ctx.translate(cx, cy);
        // Desplazamos -90° para que el segmento 0 empiece exactamente en la parte superior (Needle at 270° / Top)
        ctx.rotate(((this.currentAngle - 90) * Math.PI) / 180);

        const numSlices = this.segments.length;
        const sliceAngle = (2 * Math.PI) / numSlices;

        for (let i = 0; i < this.segments.length; i++) {
            const seg = this.segments[i];
            const startAng = i * sliceAngle;
            const endAng = startAng + sliceAngle;
            const midAng = startAng + sliceAngle / 2;

            // Forma del gajo
            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.arc(0, 0, radius, startAng, endAng);
            ctx.closePath();

            // Fondo del segmento con degradado sutil
            const sliceGrad = ctx.createRadialGradient(0, 0, radius * 0.25, 0, 0, radius);
            sliceGrad.addColorStop(0, seg.color);
            sliceGrad.addColorStop(1, this.shadeColor(seg.color, -25));
            ctx.fillStyle = sliceGrad;
            ctx.fill();

            // Borde divisorio radial dorado
            ctx.strokeStyle = "rgba(245, 158, 11, 0.4)";
            ctx.lineWidth = 2;
            ctx.stroke();

            // Contenido del gajo: Texto y Etiqueta
            ctx.save();
            ctx.rotate(midAng);
            ctx.textAlign = "right";
            ctx.textBaseline = "middle";

            const textDist = radius * 0.88;
            ctx.shadowColor = "rgba(0, 0, 0, 0.8)";
            ctx.shadowBlur = 4;

            if (seg.type === "skin") {
                // Etiqueta destacada para el premio Legendario
                ctx.fillStyle = "#fef08a";
                ctx.font = "bold 15px 'Cinzel', 'Plus Jakarta Sans', sans-serif";
                ctx.fillText(seg.label, textDist, 0);

                // Estrella mágica dorada
                ctx.font = "14px sans-serif";
                ctx.fillText("✨", textDist - 60, 0);
            } else if (seg.type === "none") {
                ctx.fillStyle = "#94a3b8";
                ctx.font = "600 13px 'Plus Jakarta Sans', sans-serif";
                ctx.fillText(seg.label, textDist, 0);
            } else if (seg.type === "orbs") {
                ctx.fillStyle = "#c7d2fe";
                ctx.font = "bold 14px 'Plus Jakarta Sans', sans-serif";
                ctx.fillText(seg.label, textDist, 0);
            } else {
                ctx.fillStyle = "#ffffff";
                ctx.font = "bold 14px 'Plus Jakarta Sans', sans-serif";
                ctx.fillText(seg.label, textDist, 0);
            }

            ctx.restore();
        }

        ctx.restore(); // Termina rotación de la rueda

        // 3. Aro dorado exterior con tachas / gemas alquímicas
        ctx.save();
        ctx.translate(cx, cy);

        // Anillo metálico dorado
        ctx.beginPath();
        ctx.arc(0, 0, radius, 0, Math.PI * 2);
        ctx.lineWidth = 10;
        ctx.strokeStyle = "#b45309";
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(0, 0, radius + 2, 0, Math.PI * 2);
        ctx.lineWidth = 3;
        ctx.strokeStyle = "#facc15";
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(0, 0, radius - 4, 0, Math.PI * 2);
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = "#78350f";
        ctx.stroke();

        // Tachas doradas decorativas
        for (let i = 0; i < numSlices; i++) {
            const ang = i * sliceAngle;
            const sx = Math.cos(ang) * (radius - 1);
            const sy = Math.sin(ang) * (radius - 1);

            ctx.fillStyle = "#fef08a";
            ctx.beginPath();
            ctx.arc(sx, sy, 3.5, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = "#78350f";
            ctx.lineWidth = 1;
            ctx.stroke();
        }
        ctx.restore();

        // 4. Dibujar la Célula de Fuego 🔥 reactiva en el centro
        this.drawCenterFireCell(cx, cy, now);

        // 5. Partículas de celebración activas
        this.updateAndDrawSparkles(ctx);

        // 6. Aguja indicadora superior apuntando hacia abajo
        this.drawTopPointer(cx, cy - radius + 4);
    }

    /**
     * Dibuja la aguja superior con estilo de flecha alquímica dorada y gema rubí.
     */
    private drawTopPointer(px: number, py: number): void {
        const ctx = this.ctx;
        ctx.save();
        ctx.translate(px, py);

        ctx.shadowColor = "rgba(0, 0, 0, 0.7)";
        ctx.shadowBlur = 6;
        ctx.shadowOffsetY = 3;

        // Cuerpo de la flecha
        ctx.beginPath();
        ctx.moveTo(0, 22); // Punta hacia abajo
        ctx.lineTo(-14, -10);
        ctx.lineTo(-6, -16);
        ctx.lineTo(6, -16);
        ctx.lineTo(14, -10);
        ctx.closePath();

        const pGrad = ctx.createLinearGradient(-14, -16, 14, 22);
        pGrad.addColorStop(0, "#fde047");
        pGrad.addColorStop(0.5, "#f59e0b");
        pGrad.addColorStop(1, "#b45309");
        ctx.fillStyle = pGrad;
        ctx.fill();

        ctx.lineWidth = 2;
        ctx.strokeStyle = "#451a03";
        ctx.stroke();

        // Gema rubí central en la base
        ctx.beginPath();
        ctx.arc(0, -7, 4.5, 0, Math.PI * 2);
        ctx.fillStyle = "#ef4444";
        ctx.fill();
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 1;
        ctx.stroke();

        ctx.restore();
    }

    /**
     * Dibuja la Célula de Fuego 🔥 animada con expresiones reactivas.
     */
    private drawCenterFireCell(cx: number, cy: number, now: number): void {
        const ctx = this.ctx;
        const cellRadius = 50;

        ctx.save();
        ctx.translate(cx, cy);

        // Respiración suave
        const breathScale = this.cellMood === "spinning"
            ? 1 + Math.sin(now * 0.02) * 0.05
            : 1 + Math.sin(now * 0.003) * 0.03;

        // Aro exterior de soporte metálico de la cápsula
        ctx.beginPath();
        ctx.arc(0, 0, cellRadius + 7, 0, Math.PI * 2);
        ctx.fillStyle = "#0f172a";
        ctx.fill();
        ctx.strokeStyle = "#f59e0b";
        ctx.lineWidth = 4;
        ctx.stroke();

        // Resplandor cálido de la Célula de Fuego
        const auraGrad = ctx.createRadialGradient(0, 0, cellRadius * 0.5, 0, 0, cellRadius * 1.3);
        auraGrad.addColorStop(0, "rgba(239, 68, 68, 0.45)");
        auraGrad.addColorStop(1, "rgba(245, 158, 11, 0)");
        ctx.fillStyle = auraGrad;
        ctx.beginPath();
        ctx.arc(0, 0, cellRadius * 1.3, 0, Math.PI * 2);
        ctx.fill();

        // Cuerpo esférico ardiente
        const bodyGrad = ctx.createRadialGradient(-cellRadius * 0.3, -cellRadius * 0.3, cellRadius * 0.1, 0, 0, cellRadius * breathScale);
        bodyGrad.addColorStop(0, "#fde047");
        bodyGrad.addColorStop(0.35, "#f97316");
        bodyGrad.addColorStop(0.85, "#ef4444");
        bodyGrad.addColorStop(1, "#991b1b");

        ctx.beginPath();
        ctx.arc(0, 0, cellRadius * breathScale, 0, Math.PI * 2);
        ctx.fillStyle = bodyGrad;
        ctx.fill();
        ctx.strokeStyle = "rgba(254, 240, 138, 0.7)";
        ctx.lineWidth = 2.5;
        ctx.stroke();

        // Llamitas decorativas pulsantes sobre la cabeza
        const flameOffset = Math.sin(now * 0.008) * 3;
        ctx.fillStyle = "#facc15";
        ctx.beginPath();
        ctx.moveTo(-10, -cellRadius * breathScale + 4);
        ctx.quadraticCurveTo(-14, -cellRadius * breathScale - 12 + flameOffset, 0, -cellRadius * breathScale - 16 + flameOffset);
        ctx.quadraticCurveTo(14, -cellRadius * breathScale - 12 + flameOffset, 10, -cellRadius * breathScale + 4);
        ctx.closePath();
        ctx.fill();

        // Expresiones faciales de la Célula de Fuego
        this.drawCellExpression(ctx, cellRadius, breathScale, now);

        ctx.restore();
    }

    /**
     * Expresiones faciales según el estado actual (idle, spinning, won_crystals, won_orbs, won_skin, empty).
     */
    private drawCellExpression(
        ctx: CanvasRenderingContext2D,
        baseR: number,
        breathScale: number,
        now: number
    ): void {
        const eyeY = -baseR * 0.08;
        const eyeSpacing = baseR * 0.34;
        const mood = this.cellMood;

        if (mood === "spinning") {
            // Ojos en espiral / mareados @_@
            const spiralAngle = now * 0.015;
            for (const s of [-1, 1]) {
                const ex = s * eyeSpacing;
                ctx.save();
                ctx.translate(ex, eyeY);
                ctx.rotate(spiralAngle);
                ctx.strokeStyle = "#1e293b";
                ctx.lineWidth = 2.2;
                ctx.beginPath();
                for (let a = 0; a < 4 * Math.PI; a += 0.2) {
                    const r = a * 1.5;
                    const x = Math.cos(a) * r;
                    const y = Math.sin(a) * r;
                    if (a === 0) ctx.moveTo(x, y);
                    else ctx.lineTo(x, y);
                }
                ctx.stroke();
                ctx.restore();
            }

            // Gotitas de sudor de anime 💧
            ctx.fillStyle = "#38bdf8";
            ctx.beginPath();
            ctx.arc(eyeSpacing + 14, eyeY - 14, 3.5, 0, Math.PI * 2);
            ctx.fill();

            // Boca en pequeña sorpresa "o"
            ctx.fillStyle = "#451a03";
            ctx.beginPath();
            ctx.arc(0, baseR * 0.24, 4.5, 0, Math.PI * 2);
            ctx.fill();

        } else if (mood === "won_crystals" || mood === "won_orbs") {
            // Ojos estrellados brillantes ✨ (Starry Eyes)
            for (const s of [-1, 1]) {
                const ex = s * eyeSpacing;
                this.drawSparkleStar(ctx, ex, eyeY, 8.5, "#ffffff", "#fef08a");
            }

            // Mejillas sonrojadas
            ctx.fillStyle = "rgba(244, 63, 94, 0.4)";
            ctx.beginPath();
            ctx.arc(-eyeSpacing, eyeY + 12, 6, 0, Math.PI * 2);
            ctx.arc(eyeSpacing, eyeY + 12, 6, 0, Math.PI * 2);
            ctx.fill();

            // Sonrisa amplia radiante
            ctx.strokeStyle = "#451a03";
            ctx.fillStyle = "#ef4444";
            ctx.lineWidth = 2.5;
            ctx.beginPath();
            ctx.arc(0, baseR * 0.16, 12, 0.1 * Math.PI, 0.9 * Math.PI);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();

        } else if (mood === "won_skin") {
            // Célula Legendaria: Corona dorada sobre la cabeza y destellos supremos
            this.drawLegendaryCrown(ctx, baseR * breathScale, now);

            // Ojos dorados gigantes
            for (const s of [-1, 1]) {
                const ex = s * eyeSpacing;
                this.drawSparkleStar(ctx, ex, eyeY, 10, "#ffffff", "#f59e0b");
            }

            // Gran sonrisa triunfal
            ctx.strokeStyle = "#451a03";
            ctx.fillStyle = "#facc15";
            ctx.lineWidth = 2.5;
            ctx.beginPath();
            ctx.arc(0, baseR * 0.18, 14, 0.05 * Math.PI, 0.95 * Math.PI);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();

        } else if (mood === "empty") {
            // Estado triste: Ojos caídos con lagrimita cómica 😢
            ctx.strokeStyle = "#1e293b";
            ctx.lineWidth = 2.5;
            ctx.lineCap = "round";

            // Ojos semicerrados tristes
            for (const s of [-1, 1]) {
                const ex = s * eyeSpacing;
                ctx.beginPath();
                ctx.arc(ex, eyeY + 3, 6, 1.1 * Math.PI, 1.9 * Math.PI);
                ctx.stroke();
            }

            // Lagrimita azul cayendo
            const tearY = eyeY + 10 + Math.sin(now * 0.005) * 4;
            ctx.fillStyle = "#38bdf8";
            ctx.beginPath();
            ctx.moveTo(eyeSpacing + 4, tearY - 4);
            ctx.quadraticCurveTo(eyeSpacing + 8, tearY, eyeSpacing + 4, tearY + 5);
            ctx.quadraticCurveTo(eyeSpacing, tearY, eyeSpacing + 4, tearY - 4);
            ctx.closePath();
            ctx.fill();

            // Boca triste curvada hacia abajo
            ctx.beginPath();
            ctx.arc(0, baseR * 0.32, 8, 1.15 * Math.PI, 1.85 * Math.PI);
            ctx.stroke();

        } else {
            // Estado IDLE: Parpadeo suave y tierna sonrisa
            const blink = Math.sin(now * 0.002) > 0.94; // Parpadeo ocasional breve

            ctx.fillStyle = "#1e293b";
            for (const s of [-1, 1]) {
                const ex = s * eyeSpacing;
                if (blink) {
                    ctx.strokeStyle = "#1e293b";
                    ctx.lineWidth = 2.2;
                    ctx.beginPath();
                    ctx.moveTo(ex - 5, eyeY);
                    ctx.lineTo(ex + 5, eyeY);
                    ctx.stroke();
                } else {
                    ctx.beginPath();
                    ctx.arc(ex, eyeY, 4.5, 0, Math.PI * 2);
                    ctx.fill();

                    // Brillo blanco en los ojos
                    ctx.fillStyle = "#ffffff";
                    ctx.beginPath();
                    ctx.arc(ex - 1.5, eyeY - 1.5, 1.6, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.fillStyle = "#1e293b";
                }
            }

            // Sonrisa tierna curvada
            ctx.strokeStyle = "#451a03";
            ctx.lineWidth = 2.2;
            ctx.lineCap = "round";
            ctx.beginPath();
            ctx.arc(0, baseR * 0.16, 7, 0.2 * Math.PI, 0.8 * Math.PI);
            ctx.stroke();
        }
    }

    /**
     * Dibuja una estrella de 4 puntas centellante.
     */
    private drawSparkleStar(
        ctx: CanvasRenderingContext2D,
        cx: number,
        cy: number,
        size: number,
        innerColor: string,
        outerColor: string
    ): void {
        ctx.save();
        ctx.translate(cx, cy);

        ctx.fillStyle = outerColor;
        ctx.beginPath();
        for (let i = 0; i < 4; i++) {
            const a = (i * Math.PI) / 2;
            ctx.lineTo(Math.cos(a) * size, Math.sin(a) * size);
            const aMid = a + Math.PI / 4;
            ctx.lineTo(Math.cos(aMid) * (size * 0.35), Math.sin(aMid) * (size * 0.35));
        }
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = innerColor;
        ctx.beginPath();
        ctx.arc(0, 0, size * 0.3, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
    }

    /**
     * Dibuja una corona real sobre la célula al ganar el Skin Fénix.
     */
    private drawLegendaryCrown(ctx: CanvasRenderingContext2D, cellR: number, now: number): void {
        const bob = Math.sin(now * 0.007) * 3;
        const crownW = 28;
        const baseY = -cellR * 0.95 + bob;
        const topY = -cellR * 1.45 + bob;

        ctx.save();
        ctx.fillStyle = "#fbbf24";
        ctx.strokeStyle = "#b45309";
        ctx.lineWidth = 2;

        ctx.beginPath();
        ctx.moveTo(-crownW, baseY);
        ctx.lineTo(-crownW * 1.15, topY + 6);
        ctx.lineTo(-crownW * 0.45, baseY - 6);
        ctx.lineTo(0, topY);
        ctx.lineTo(crownW * 0.45, baseY - 6);
        ctx.lineTo(crownW * 1.15, topY + 6);
        ctx.lineTo(crownW, baseY);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        // Rubí en la punta central de la corona
        ctx.fillStyle = "#ef4444";
        ctx.beginPath();
        ctx.arc(0, topY + 4, 3, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
    }

    /**
     * Genera destellos de victoria.
     */
    private spawnCelebrationSparkles(mood: CellMood): void {
        this.sparkles = [];
        const count = mood === "won_skin" ? 45 : 25;
        const colors = mood === "won_skin"
            ? ["#facc15", "#f97316", "#ef4444", "#fef08a"]
            : mood === "won_orbs"
                ? ["#818cf8", "#c7d2fe", "#a855f7", "#38bdf8"]
                : ["#38bdf8", "#0284c7", "#facc15", "#ffffff"];

        const cx = this.canvas.width / 2;
        const cy = this.canvas.height / 2;

        for (let i = 0; i < count; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = 1.5 + Math.random() * 4.5;
            this.sparkles.push({
                x: cx,
                y: cy,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                color: colors[Math.floor(Math.random() * colors.length)],
                size: 2.5 + Math.random() * 4.5,
                alpha: 1,
                life: 1,
            });
        }
    }

    private updateAndDrawSparkles(ctx: CanvasRenderingContext2D): void {
        for (let i = this.sparkles.length - 1; i >= 0; i--) {
            const p = this.sparkles[i];
            p.x += p.vx;
            p.y += p.vy;
            p.vy += 0.04; // gravedad ligera
            p.alpha -= 0.016;

            if (p.alpha <= 0) {
                this.sparkles.splice(i, 1);
                continue;
            }

            ctx.save();
            ctx.globalAlpha = Math.max(0, p.alpha);
            ctx.fillStyle = p.color;
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
        }
    }

    private shadeColor(color: string, percent: number): string {
        if (!color.startsWith("#")) return color;
        const num = parseInt(color.slice(1), 16);
        const amt = Math.round(2.55 * percent);
        const R = (num >> 16) + amt;
        const G = ((num >> 8) & 0x00ff) + amt;
        const B = (num & 0x0000ff) + amt;
        return (
            "#" +
            (
                0x1000000 +
                (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
                (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
                (B < 255 ? (B < 1 ? 0 : B) : 255)
            )
                .toString(16)
                .slice(1)
        );
    }
}
